import React, { useState, useEffect } from 'react';
import githubService from '../utils/githubService';
import QRCode from 'qrcode';

const DeploymentModal = ({ 
  isOpen, 
  htmlContent, 
  fileName, 
  contentType = 'template', // 'flashcard', 'quiz', 'reading', 'questions' 등
  onClose,
  onDeploymentStart = null, // 배포/다운로드 시작 시 호출되는 콜백
  enableLiveShare = false // 실시간 링크 공유 활성화 여부 (진로경로 등에서 사용)
}) => {
  const [githubUser, setGithubUser] = useState(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [deploymentStatus, setDeploymentStatus] = useState(null); // 'uploading', 'waiting', 'ready', 'timeout'
  const [liveShareResult, setLiveShareResult] = useState(null); // 실시간 링크 공유 결과

  // GitHub 로그인 상태 확인
  useEffect(() => {
    if (isOpen) {
      const checkGitHubLogin = async () => {
        const token = localStorage.getItem('github_access_token');
        if (token && githubService.isLoggedIn()) {
          setGithubUser(githubService.getUserInfo());
        } else if (token) {
          try {
            const userInfo = await githubService.initializeWithToken(token);
            setGithubUser(userInfo);
          } catch (error) {
            console.log('GitHub 토큰 만료:', error);
            localStorage.removeItem('github_access_token');
            setGithubUser(null);
          }
        }
      };
      checkGitHubLogin();
    }
  }, [isOpen]);

  // GitHub 로그인
  const handleGitHubLogin = () => {
    localStorage.setItem('github_login_return_url', window.location.pathname + window.location.search);
    const authUrl = githubService.getAuthUrl();
    window.location.href = authUrl;
  };

  // HTML 다운로드
  const handleDownload = () => {
    if (!htmlContent || !fileName) {
      alert('다운로드할 콘텐츠가 없습니다.');
      return;
    }

    // 배포/다운로드 시작 콜백 호출 (부모의 문항 수정 모달 닫기)
    if (onDeploymentStart) {
      onDeploymentStart();
    }

    setIsDownloading(true);
    
    try {
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ 파일 다운로드 완료:', fileName);
    } catch (error) {
      console.error('❌ 다운로드 실패:', error);
      alert('다운로드 실패: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  // GitHub Pages 배포
  const handleGitHubDeploy = async () => {
    if (!githubUser) {
      alert('GitHub 로그인이 필요합니다.');
      return;
    }

    if (!htmlContent || !fileName) {
      alert('배포할 콘텐츠가 없습니다.');
      return;
    }

    // 배포/다운로드 시작 콜백 호출 (부모의 문항 수정 모달 닫기)
    if (onDeploymentStart) {
      onDeploymentStart();
    }

    setIsDeploying(true);
    setDeploymentStatus('uploading');
    
    try {
      console.log('🚀 GitHub Pages 자동 배포 시작...');
      
      // 콘텐츠 타입에 따른 폴더 구조 설정
      const folderMap = {
        'flashcard': 'flashcards',
        'quiz': 'quizzes', 
        'reading': 'reading',
        'questions': 'questions',
        'shadowing': 'shadowing',
        'template': 'templates'
      };
      
      const folder = folderMap[contentType] || 'others';
      const filePath = `${folder}/${fileName}`;
      
      // GitHub에 자동 배포 (업로드)
      const result = await githubService.autoDeployHtml(htmlContent, filePath);
      
      // 배포 상태를 대기 중으로 변경
      setDeploymentStatus('waiting');
      
      // GitHub Pages 배포 완료까지 대기 (최대 30초)
      console.log('⏳ GitHub Pages 배포 상태 확인 시작...');
      const deploymentStatus = await githubService.waitForPageDeployment(undefined, 30000); // 30초로 단축
      
      if (deploymentStatus.ready) {
        setDeploymentStatus('ready');
      } else if (deploymentStatus.status === 'timeout') {
        setDeploymentStatus('timeout');
      } else {
        setDeploymentStatus('error');
      }
      
      setDeploymentResult({
        ...result,
        deploymentReady: deploymentStatus.ready,
        deploymentMessage: deploymentStatus.message
      });
      
      console.log('🎉 배포 프로세스 완료!');
      
    } catch (error) {
      console.error('❌ GitHub 배포 실패:', error);
      setDeploymentStatus('error');
      alert(`GitHub 배포 실패: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  // 실시간 링크 공유 (React 앱 URL 직접 공유)
  const handleLiveShare = async () => {
    try {
      // 배포/다운로드 시작 콜백 호출
      if (onDeploymentStart) {
        onDeploymentStart();
      }

      const currentUrl = window.location.href;
      const shareUrl = currentUrl.includes('?') ? currentUrl : currentUrl + window.location.search;
      
      // QR 코드 생성
      const qrCodeDataUrl = await QRCode.toDataURL(shareUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setLiveShareResult({
        url: shareUrl,
        qrCode: qrCodeDataUrl,
        title: getContentTitle(contentType),
        timestamp: new Date().toLocaleString('ko-KR')
      });

    } catch (error) {
      console.error('❌ 실시간 링크 생성 실패:', error);
      alert('링크 생성 실패: ' + error.message);
    }
  };

  // 콘텐츠 타입별 제목 생성
  const getContentTitle = (type) => {
    const titles = {
      'flashcard': '단어카드 학습',
      'quiz': '어휘 퀴즈',
      'reading': '읽기 학습',
      'questions': '문항 연습',
      'template': 'AI 맞춤 진로경로',
      'career-path': 'AI 진로분석'
    };
    return titles[type] || '영어 학습 자료';
  };

  // 모달 닫기
  const handleClose = () => {
    setDeploymentResult(null);
    setDeploymentStatus(null);
    setLiveShareResult(null);
    onClose();
  };

  if (!isOpen) return null;

  // 실시간 링크 공유 결과 표시
  if (liveShareResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-blue-800 mb-2">실시간 링크 생성 완료!</h2>
            <p className="text-gray-600 mb-1">{liveShareResult.title} 링크를 공유하세요.</p>
            <p className="text-xs text-gray-500 mb-3">생성 시간: {liveShareResult.timestamp}</p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3">
              <p className="text-sm text-green-800">
                🌐 실시간 연결된 링크
              </p>
              <p className="text-xs text-green-600">
                완전한 인터랙티브 기능 체험 가능 (차트, 애니메이션 등)
              </p>
            </div>
            
            {/* QR Code */}
            <div className="mb-3">
              <img 
                src={liveShareResult.qrCode} 
                alt="QR Code"
                className="mx-auto mb-1 border border-gray-200 rounded w-32 h-32"
              />
              <p className="text-xs text-gray-500">QR 코드로 모바일 접근</p>
            </div>
            
            {/* Link */}
            <div className="bg-gray-50 p-2 rounded-lg mb-3">
              <p className="text-sm text-gray-600 mb-2">공유 링크:</p>
              <a 
                href={liveShareResult.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-sm break-all"
              >
                {liveShareResult.url}
              </a>
            </div>
            
            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(liveShareResult.url);
                  alert('링크가 클립보드에 복사되었습니다!');
                }}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                링크 복사
              </button>
              <button
                onClick={() => window.open(liveShareResult.url, '_blank')}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                새 탭에서 열기
              </button>
            </div>
            
            <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
              💡 <strong>장점:</strong> 완전한 React 앱으로 모든 기능 정상 작동
            </div>
            
            <button
              onClick={handleClose}
              className="mt-2 text-gray-500 hover:text-gray-700 text-sm"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 배포 중 진행 상태 표시
  if (isDeploying && deploymentStatus) {
    const statusMessages = {
      'uploading': {
        title: '파일 업로드 중...',
        message: 'GitHub 저장소에 파일을 업로드하고 있습니다.',
        icon: '📤'
      },
      'waiting': {
        title: '사이트 준비 중...',
        message: 'GitHub Pages 배포가 완료될 때까지 기다리고 있습니다.',
        icon: '⏳'
      },
      'error': {
        title: '배포 실패',
        message: '배포 중 오류가 발생했습니다.',
        icon: '❌'
      }
    };

    const currentStatus = statusMessages[deploymentStatus];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="text-center">
            <div className="text-6xl mb-4">{currentStatus?.icon}</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{currentStatus?.title}</h2>
            <p className="text-gray-600 mb-6">{currentStatus?.message}</p>
            
            {deploymentStatus === 'uploading' && (
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}
            
            {deploymentStatus === 'waiting' && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">최대 30초 후 링크를 제공합니다</p>
                <p className="text-xs text-gray-400 mt-1">배포가 완료되지 않아도 사용 가능할 수 있습니다</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 배포 결과 표시 중
  if (deploymentResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="text-center">
            <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-green-800 mb-2">배포 완료!</h2>
            <p className="text-gray-600 mb-2">GitHub Pages에 성공적으로 배포되었습니다.</p>
            
            {/* 배포 상태에 따른 추가 메시지 */}
            {deploymentResult.deploymentReady === false && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  🔄 GitHub Pages 배포 진행 중
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  링크를 클릭한 후 페이지가 로딩되지 않으면 1-2분 후 새로고침해주세요.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  처음 배포하는 저장소는 최대 10분까지 소요될 수 있습니다.
                </p>
              </div>
            )}
            
            {deploymentResult.deploymentReady === true && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-800">
                  ✅ 배포가 완료되었습니다! 바로 접근 가능합니다.
                </p>
              </div>
            )}
            
            {/* QR Code */}
            {deploymentResult.qrCode && (
              <div className="mb-4">
                <img 
                  src={deploymentResult.qrCode} 
                  alt="QR Code"
                  className="mx-auto mb-2 border border-gray-200 rounded"
                />
                <p className="text-xs text-gray-500">QR 코드로 모바일에서 접근</p>
              </div>
            )}
            
            {/* Link */}
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-2">배포된 링크:</p>
              <a 
                href={deploymentResult.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-sm break-all"
              >
                {deploymentResult.url}
              </a>
            </div>
            
            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(deploymentResult.url);
                  alert('링크가 클립보드에 복사되었습니다!');
                }}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                링크 복사
              </button>
              <button
                onClick={() => window.open(deploymentResult.url, '_blank')}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                title={deploymentResult.deploymentReady === false ? "페이지가 로딩되지 않으면 1-2분 후 새로고침하세요" : "바로 접근 가능합니다"}
              >
                사이트 열기
              </button>
            </div>
            
            {/* 사용자 안내 추가 */}
            {deploymentResult.deploymentReady === false && (
              <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                💡 <strong>팁:</strong> 페이지가 404 오류를 보이면 1-2분 후 브라우저에서 새로고침(F5)을 눌러주세요.
              </div>
            )}
            
            <button
              onClick={handleClose}
              className="mt-3 text-gray-500 hover:text-gray-700 text-sm"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 메인 배포/다운로드 선택 모달
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">콘텐츠 내보내기</h2>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isDeploying || isDownloading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* GitHub 로그인 상태 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">GitHub 계정</p>
            {githubUser ? (
              <div className="flex items-center space-x-2">
                <img 
                  src={githubUser.avatar_url} 
                  alt={githubUser.login}
                  className="w-6 h-6 rounded-full"
                />
                <span style={{ color: 'black' }} className="text-sm font-medium">{githubUser.login}</span>
                <span className="text-xs text-green-600">로그인됨</span>
              </div>
            ) : (
              <button
                onClick={handleGitHubLogin}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                GitHub 로그인 필요
              </button>
            )}
          </div>

          {/* 파일 정보 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">파일명</p>
            <p className="font-medium text-sm break-all" title={fileName}>
              {fileName.length > 30 ? `${fileName.substring(0, 30)}...` : fileName}
            </p>
            <p className="text-xs text-gray-500 mt-1">타입: {contentType}</p>
          </div>

          {/* 버튼들 */}
          <div className="space-y-3">
            {/* 실시간 링크 공유 버튼 (enableLiveShare가 true일 때만 표시) */}
            {enableLiveShare && (
              <button
                onClick={handleLiveShare}
                disabled={isDeploying || isDownloading}
                className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span>🌐 실시간 링크 공유 (추천)</span>
              </button>
            )}

            {/* GitHub 배포 버튼 */}
            <button
              onClick={handleGitHubDeploy}
              disabled={!githubUser || isDeploying || isDownloading}
              className="w-full px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {isDeploying && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
              </svg>
              <span>{isDeploying ? '배포 중...' : 'GitHub Pages 배포'}</span>
            </button>

            {/* HTML 다운로드 버튼 */}
            <button
              onClick={handleDownload}
              disabled={isDeploying || isDownloading}
              className="w-full px-4 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {isDownloading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{isDownloading ? '다운로드 중...' : 'HTML 파일 다운로드'}</span>
            </button>
          </div>

          {/* 도움말 */}
          <div className="text-xs text-gray-500 space-y-1">
            {enableLiveShare && (
              <>
                <p>• 실시간 링크: 완전한 기능으로 즉시 공유 (추천)</p>
                <p>• GitHub Pages: HTML 파일로 영구 저장 및 공유</p>
                <p>• HTML 다운로드: 오프라인 사용 (기능 제한)</p>
              </>
            )}
            {!enableLiveShare && (
              <>
                <p>• GitHub Pages: 링크와 QR코드로 즉시 공유 가능</p>
                <p>• HTML 다운로드: 로컬 파일로 저장</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentModal;